import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './MalwareBazaarHistory.css';

const MalwareBazaarHistory = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [stats, setStats] = useState({
    totalSubmissions: 0,
    todaySubmissions: 0,
    submitted: 0,
    failed: 0,
    alreadyKnown: 0
  });

  useEffect(() => {
    fetchSubmissions();
    fetchStatistics();
  }, [selectedStatus, currentPage]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_URL;

      let url = `${API_BASE_URL}/malware-bazaar?page=${currentPage}&limit=10`;
      if (selectedStatus !== 'all') {
        url += `&status=${selectedStatus}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions || []);
      } else {
        console.error('Failed to fetch submissions:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_URL;

      const response = await fetch(`${API_BASE_URL}/malware-bazaar/statistics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats({
          totalSubmissions: data.summary?.totalSubmissions || 0,
          todaySubmissions: data.summary?.todaySubmissions || 0,
          submitted: data.byStatus?.submitted || 0,
          failed: data.byStatus?.failed || 0,
          alreadyKnown: data.byStatus?.alreadyKnown || 0
        });
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Submitted to MalwareBazaar':
        return 'status-submitted';
      case 'Pending':
        return 'status-pending';
      case 'Processing':
        return 'status-processing';
      case 'Failed':
        return 'status-failed';
      case 'Already Known':
        return 'status-known';
      default:
        return 'status-unknown';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="malware-history-container">
      <button onClick={() => navigate('/tools')} className="back-button-history">
        ← Back to Dashboard
      </button>

      <div className="history-header">
        <h1>🦠 Malware Bazaar Submissions</h1>
        <button className="btn-upload" onClick={() => navigate('/malware-bazaar')}>
          📤 New Submission
        </button>
      </div>

      {/* Statistics */}
      <div className="statistics-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.totalSubmissions}</div>
          <div className="stat-label">Total Submissions</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.todaySubmissions}</div>
          <div className="stat-label">Today</div>
        </div>
        <div className="stat-card success">
          <div className="stat-value">{stats.submitted}</div>
          <div className="stat-label">Successfully Submitted</div>
        </div>
        <div className="stat-card failed">
          <div className="stat-value">{stats.failed}</div>
          <div className="stat-label">Failed</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.alreadyKnown}</div>
          <div className="stat-label">Already Known</div>
        </div>
      </div>

      {/* Filter */}
      <div className="filter-section">
        <label htmlFor="status-filter">Filter by Status:</label>
        <select
          id="status-filter"
          value={selectedStatus}
          onChange={(e) => {
            setSelectedStatus(e.target.value);
            setCurrentPage(1);
          }}
          className="status-filter"
        >
          <option value="all">All Statuses</option>
          <option value="Pending">Pending</option>
          <option value="Processing">Processing</option>
          <option value="Submitted to MalwareBazaar">Submitted to MalwareBazaar</option>
          <option value="Failed">Failed</option>
          <option value="Already Known">Already Known</option>
        </select>
      </div>

      {/* Submissions Table */}
      {loading ? (
        <div className="loading">Loading submissions...</div>
      ) : submissions.length === 0 ? (
        <div className="no-data">
          <p>No submissions found</p>
          <button onClick={() => navigate('/malware-bazaar')}>Create your first submission</button>
        </div>
      ) : (
        <div className="submissions-table-wrapper">
          <table className="submissions-table">
            <thead>
              <tr>
                <th>Case #</th>
                <th>File Name</th>
                <th>Submitted By</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((submission) => (
                <tr key={submission._id}>
                  <td className="case-number">{submission.caseNumber}</td>
                  <td className="file-name">
                    <span title={submission.fileName}>{submission.fileName}</span>
                  </td>
                  <td className="email">{submission.email}</td>
                  <td className="status">
                    <span className={`badge ${getStatusBadgeClass(submission.status)}`}>
                      {submission.status}
                    </span>
                  </td>
                  <td className="date">{formatDate(submission.createdAt)}</td>
                  <td className="actions">
                    <button
                      className="btn-view"
                      onClick={() => navigate(`/malware-bazaar/detail/${submission.caseNumber}`)}
                      title="View Details"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Info Section */}
      <div className="info-box">
        <h3>📋 About Malware Bazaar</h3>
        <p>
          Malware Bazaar is a free malware collection portal hosted by abuse.ch. It serves as a central platform for sharing and analyzing malware samples. When you submit a malware sample here, it goes directly to their database which is used by security researchers worldwide.
        </p>
        <p>
          <strong>Important:</strong> Only submit confirmed malware, not suspicious or benign files. Fresh samples (less than 10 days old) are preferred.
        </p>
      </div>
    </div>
  );
};

export default MalwareBazaarHistory;
